import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { SmartGridComponent, ColDef, GridConfig, SelectionChangedEvent } from 'app/shared/components/smart-grid';
import { MOCK_ORDERS, MockOrder } from '../mock-data/mock-orders.data';

/**
 * Row Selection Example
 * Demonstrates row selection with checkboxes and multi-select functionality
 */
@Component({
  selector: 'app-selection-example',
  standalone: true,
  imports: [CommonModule, SmartGridComponent, MatButtonModule, MatIconModule],
  template: `
    <div class="p-6">
      <div class="mb-4">
        <h2 class="text-2xl font-bold mb-2">Ejemplo de Selección de Filas</h2>
        <p class="text-gray-600 mb-2">
          Selección de múltiples filas con checkboxes, soporte para Shift+Click, y select all.
        </p>
        <div class="flex gap-2 flex-wrap">
          <span class="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold">
            Checkbox Column
          </span>
          <span class="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
            Select All
          </span>
          <span class="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-semibold">
            Shift+Click Range
          </span>
          <span class="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-semibold">
            Visual Feedback
          </span>
        </div>
      </div>

      <!-- Selection Actions -->
      <div class="mb-4 flex gap-2 flex-wrap items-center">
        <button
          mat-raised-button
          color="primary"
          (click)="selectAll()">
          <mat-icon>check_box</mat-icon>
          Seleccionar Todo
        </button>
        <button
          mat-raised-button
          (click)="deselectAll()">
          <mat-icon>check_box_outline_blank</mat-icon>
          Deseleccionar Todo
        </button>
        <button
          mat-raised-button
          color="accent"
          [disabled]="selectedCount === 0"
          (click)="exportSelected()">
          <mat-icon>file_download</mat-icon>
          Exportar Selección ({{ selectedCount }})
        </button>
        <button
          mat-raised-button
          color="warn"
          [disabled]="selectedCount === 0"
          (click)="deleteSelected()">
          <mat-icon>delete</mat-icon>
          Eliminar Selección ({{ selectedCount }})
        </button>
      </div>

      <!-- Selection Summary -->
      <div class="mb-4 p-3 bg-blue-50 dark:bg-blue-900 rounded flex items-center gap-2">
        <mat-icon class="text-blue-600 dark:text-blue-300">info</mat-icon>
        <span class="text-sm font-semibold">
          {{ selectedCount }} de {{ totalCount }} registros seleccionados
        </span>
      </div>

      <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
        <app-smart-grid
          [data]="orders"
          [columnDefs]="columnDefs"
          [config]="gridConfig"
          (selectionChanged)="onSelectionChanged($event)">
        </app-smart-grid>
      </div>

      <div class="mt-4 p-4 bg-blue-50 dark:bg-blue-900 rounded">
        <h3 class="font-semibold mb-2">💡 Cómo usar la selección:</h3>
        <ul class="text-sm space-y-1 list-disc list-inside">
          <li>Haz clic en el checkbox del encabezado para seleccionar/deseleccionar todo</li>
          <li>Haz clic en el checkbox de una fila para seleccionar/deseleccionar individualmente</li>
          <li>Mantén presionado Shift y haz clic en otra fila para seleccionar un rango</li>
          <li>Las filas seleccionadas se resaltan con fondo azul claro</li>
          <li>La selección persiste a través de paginación, filtros y búsqueda</li>
          <li>El estado de indeterminado (guión) aparece cuando algunas filas están seleccionadas</li>
        </ul>
      </div>

      <div class="mt-4 p-4 bg-green-50 dark:bg-green-900 rounded">
        <h3 class="font-semibold mb-2">🎯 Ejemplos de uso:</h3>
        <ul class="text-sm space-y-2 list-disc list-inside">
          <li><strong>Operaciones en lote:</strong> Selecciona múltiples registros y aplica una acción (exportar, eliminar, actualizar estado, etc.)</li>
          <li><strong>Comparación:</strong> Selecciona varios elementos para compararlos lado a lado</li>
          <li><strong>Reportes personalizados:</strong> Selecciona los datos específicos que deseas incluir en un reporte</li>
          <li><strong>Mover/Copiar datos:</strong> Selecciona y arrastra datos a otra sección (cuando se combine con drag & drop)</li>
        </ul>
      </div>

      <!-- Selected Items Preview -->
      <div *ngIf="selectedCount > 0" class="mt-4 p-4 bg-purple-50 dark:bg-purple-900 rounded">
        <h3 class="font-semibold mb-2">📋 Previsualización de selección:</h3>
        <div class="max-h-40 overflow-y-auto">
          <div *ngFor="let order of selectedOrders" class="text-sm py-1 border-b border-purple-200 dark:border-purple-700">
            <span class="font-semibold">{{ order.serial }}</span> -
            {{ order.soldTo.name }} -
            {{ order.total.toFixed(2) }}
          </div>
        </div>
      </div>

      <div class="mt-4 p-4 bg-purple-50 dark:bg-purple-900 rounded">
        <h3 class="font-semibold mb-2">📝 Código de este ejemplo:</h3>
        <pre class="text-xs overflow-x-auto"><code>{{exampleCode}}</code></pre>
      </div>
    </div>
  `,
})
export class SelectionExampleComponent {
  orders: MockOrder[] = MOCK_ORDERS;
  selectedOrders: MockOrder[] = [];
  selectedCount: number = 0;
  totalCount: number = MOCK_ORDERS.length;

  columnDefs: ColDef<MockOrder>[] = [
    {
      field: 'serial',
      headerName: 'Nro. Documento',
      width: 180,
    },
    {
      field: 'soldTo.name',
      headerName: 'Cliente',
      width: 250,
    },
    {
      field: 'class.name',
      headerName: 'Clase',
      width: 150,
    },
    {
      field: 'soldTo.address.city',
      headerName: 'Ciudad',
      width: 150,
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 120,
      valueFormatter: (value) => `${value.toFixed(2)}`,
    },
    {
      field: 'createdAt',
      headerName: 'Fecha',
      width: 150,
      valueFormatter: (value) => new Date(value).toLocaleDateString(),
    },
  ];

  gridConfig: GridConfig<MockOrder> = {
    pagination: {
      enabled: true,
      pageSize: 20,
    },
    sorting: {
      enabled: true,
      multiColumn: true,
    },
    selection: {
      enabled: true,                  // ✨ Enable row selection
      mode: 'multiple',               // Allow multiple row selection
      showCheckboxColumn: true,       // Show checkbox column
      selectOnRowClick: false,        // Don't select on row click (only checkbox)
      rememberSelection: true,        // Remember selection across pagination
    },
    styling: {
      maxHeight: '600px',
      stripedRows: true,
    },
  };

  /**
   * Handle selection change event
   */
  onSelectionChanged(event: SelectionChangedEvent<MockOrder>): void {
    this.selectedOrders = event.selectedRows;
    this.selectedCount = event.selectedRows.length;
  }

  /**
   * Select all rows (example of programmatic API)
   */
  selectAll(): void {
    // This would be called via grid API: this.gridComponent.selectAll()
    // For demo purposes, we're showing the button exists
  }

  /**
   * Deselect all rows
   */
  deselectAll(): void {
    // This would be called via grid API: this.gridComponent.deselectAll()
  }

  /**
   * Export selected rows
   */
  exportSelected(): void {
    alert(`Exportando ${this.selectedCount} registros...`);
  }

  /**
   * Delete selected rows
   */
  deleteSelected(): void {
    const confirmDelete = confirm(`¿Está seguro que desea eliminar ${this.selectedCount} registros?`);
    if (confirmDelete) {
      alert(`Se eliminarían ${this.selectedCount} registros`);
      // In real app: this.gridComponent.deselectAll()
    }
  }

  exampleCode = `// Component Code
columnDefs: ColDef<MockOrder>[] = [
  { field: 'serial', headerName: 'Nro. Documento' },
  { field: 'soldTo.name', headerName: 'Cliente' },
  { field: 'class.name', headerName: 'Clase' },
  { field: 'total', headerName: 'Total' }
];

gridConfig: GridConfig<MockOrder> = {
  selection: {
    enabled: true,              // ✨ Enable row selection
    mode: 'multiple',           // 'single' or 'multiple'
    showCheckboxColumn: true,   // Show checkbox column
    selectOnRowClick: false,    // Select via checkbox only
    rememberSelection: true     // Remember across pagination
  },
  pagination: { enabled: true, pageSize: 20 },
  sorting: { enabled: true, multiColumn: true }
};

// Template
<app-smart-grid
  [data]="orders"
  [columnDefs]="columnDefs"
  [config]="gridConfig"
  (selectionChanged)="onSelectionChanged($event)">
</app-smart-grid>

// Handle selection changes
onSelectionChanged(event: SelectionChangedEvent<MockOrder>): void {
}

// Features:
// - Checkbox column with select all in header
// - Individual row selection via checkbox
// - Shift+Click for range selection
// - Visual feedback (blue highlight) for selected rows
// - Selection persists across pagination
// - Indeterminate state when some rows selected
// - Single or multiple selection modes
// - Optional select on row click
// - Programmatic API: selectAll(), deselectAll(), getSelectedRows()`;
}
